/**
 * **Note: `@e3dc-react/shell/Types` also exports an `ApiResult` class. It is structurally identical
 * but NOT the same class — usePortalQuery/queryFnWithApiResult unwrap via `instanceof` against
 * THIS class, so api functions must import ApiResult from here (not from the shell package) or
 * their query data will silently be the unwrapped-wrapper object instead of `.value`.**
 *
 * Default container for providing a typed container as a result for api client implementations
 *
 * These containers are used to enable to always use clear typed objects for handling positive or negative results.
 *
 * It is to prevent coding patterns where a check for null values happens, or with try/catch is used to lazy handle
 * appearing errors
 *
 * Example:
 *
 *
 * const findAnything = async (): Promise<ApiResult<SomeModel>> =>
 *   return apiClient.findSomething().then((result: AxiosResponse)=>{
 *    if(result?.statusCode === 200){
 *      return new ApiResult<SomeModel>(result.data as SomeModel, false)
 *    }
 *   }).catch(()=>{
 *     return new ApiResult<SomeModel>(null, true)
 *   })
 * }
 *
 * const result = await findAnything()
 *
 * if(result.hasErrors){
 *   console.error('an unexpected error appeared')
 * }else{
 *   console.log('all good, continue')
 * }
 *
 */
export class ApiResult<T, E = unknown> {
  value: T | null
  hasErrors: boolean
  error: E | undefined
  constructor(value: T | null = null, hasErrors = false, error?: E) {
    this.value = value
    this.hasErrors = hasErrors
    this.error = error
  }
}

/**
 * The signature used to determine the current state of a Device.
 *
 * It is part of the applications domain model. It does not match the signature of the equivalent api resources.
 *
 * This signature is mostly by the domain models of the Installations settings
 */
export interface DeviceState {
  connected: boolean
  working: boolean
  inService: boolean
}

export enum ApiAccountType {
  user = 'user',
  company = 'company',
  cloud = 'cloud',
}
export enum ApiAccountSourceType {
  e3dc = 'e3dc',
  hager = 'hager',
}
export enum ApiAccountRole {
  INSTALLER = 'installer',
  USER = 'user',
  OWNER = 'owner',
  SALES = 'sales',
}

export enum ApiInviteType {
  invite = 'invite',
  handover = 'handover',
  handoverPlanned = 'handover-planned',
}

export enum ApiPermissionType {
  ro = 'ro',
  rw = 'rw',
  noAccess = 'no-access',
  luoxEnergy = 'lumenaza',
  cloudinterface = 'cloudinterface',
  pending = 'pending',
  blocked = 'blocked',
}

export enum ExtensionsType {
  luoxEnergy = 'lumenaza',
  cloudinterface = 'cloudinterface',
}
